import { Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

/**
 * A link card in the right column (design §2.3/§4.0): counter, "+ Toevoegen" and at
 * most `max` rows, then "Alle n tonen". The same button folds the list up again, so it
 * never disappears under a keyboard user's focus.
 */
export function AssociationCard<T>({
  title,
  items,
  itemKey,
  renderItem,
  onAdd,
  addLabel = 'Toevoegen',
  emptyText,
  max = 5,
  footer,
}: {
  title: string
  items: T[]
  itemKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  onAdd?: () => void
  addLabel?: string
  emptyText: string
  max?: number
  footer?: ReactNode
}) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? items : items.slice(0, max)
  return (
    <section aria-label={title} className="vonk-card flex flex-col gap-2 p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {title} ({items.length})
        </h2>
        {onAdd ? (
          <Button type="button" size="icon-sm" variant="ghost" aria-label={addLabel} onClick={onAdd}>
            <Plus />
          </Button>
        ) : null}
      </header>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {visible.map((item) => (
            <li key={itemKey(item)}>{renderItem(item)}</li>
          ))}
        </ul>
      )}
      {items.length > max ? (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="self-start text-xs font-medium text-primary hover:underline"
        >
          {showAll ? 'Minder tonen' : `Alle ${items.length} tonen`}
        </button>
      ) : null}
      {footer}
    </section>
  )
}
